import { useState } from 'react';
import { type SeoLockName, type SeoLocks } from './ContentItemEditor.types';

/*
    Which SEO fields still follow the course, and what they follow.

    The Angular form worked this out on load: a field was locked when its value already matched the
    value it would derive. An admin who had written the field by hand saw it unlocked, because the
    values differed. That rule is here, applied once from the values the page loaded with.
*/
export function initialSeoLocks({
    itemTitle,
    itemDescription,
    seoTitle,
    seoDescription,
    canonicalUrl,
    derivedCanonicalUrl,
}: {
    itemTitle: string;
    itemDescription: string;
    seoTitle: string;
    seoDescription: string;
    canonicalUrl: string;
    derivedCanonicalUrl: string;
}): SeoLocks {
    return {
        title: itemTitle === seoTitle,
        description: itemDescription === seoDescription,

        // An empty canonical URL counts as following, because a course that has never been saved
        // with one should pick up the derived value.
        canonicalUrl: !canonicalUrl || canonicalUrl === derivedCanonicalUrl,
    };
}

/*
    Takes a thunk, which useState runs once. The locks are read from the values the page loaded
    with, so a later keystroke must not relock a field the admin unlocked.
*/
export function useSeoFieldLocks(initial: () => SeoLocks) {
    const [locks, setLocks] = useState(initial);

    function toggle(name: SeoLockName) {
        setLocks(current => ({ ...current, [name]: !current[name] }));
    }

    return { locks, toggle };
}
